import { useField } from 'formik';
import { isString } from 'lodash-es';
import React, { useCallback, useState, useEffect } from 'react';

import useDebounceWithOnBlur from 'hooks/useDebounceWithOnBlur';
import * as constants from 'utils/constants';

import { ComponentProps, TextAreaComponentProps } from './interfaces';
import * as Styled from './styled';

export const TextArea = React.forwardRef<HTMLFormElement, TextAreaComponentProps>(
  function TextAreaBase(
    {
      ariaLabel = '',
      hint,
      label,
      mode = 'Normal',
      maxLength = 100,
      maxRows = 3,
      name,
      error,
      errorLabelProps,
      ...props
    },
    ref
  ) {
    return (
      <Styled.Wrapper>
        <Styled.Label htmlFor={name} id={`${name}.label`}>
          {label}
        </Styled.Label>
        <Styled.TextArea
          ref={ref}
          name={name}
          mode={mode}
          minRows={mode === 'Input' ? 1 : 6}
          maxRows={mode === 'Input' ? maxRows : Number.POSITIVE_INFINITY}
          aria-label={ariaLabel || label}
          aria-invalid={!!error}
          aria-labelledby={label ? `${name}.label` : props['aria-labelledby']}
          maxLength={maxLength}
          error={error ? 'true' : 'false'}
          {...props}
        />
        {error && <Styled.ErrorLabel {...errorLabelProps}>{error}</Styled.ErrorLabel>}
        {hint && <Styled.HintLabel>{hint}</Styled.HintLabel>}
      </Styled.Wrapper>
    );
  }
);

const TextAreaWithFormik: React.FC<ComponentProps> = ({
  delay = constants.INPUT_DELAY,
  name,
  ...props
}) => {
  const [field, meta] = useField(name);
  const [innerValue, setInnerValue] = useState('');

  const [changeEventRef, handleOnBlur] = useDebounceWithOnBlur({
    delay,
    field,
    // trim the whitespace from the ends of the text area input
    doOnBlur: eventRefCurrent => {
      if (isString(eventRefCurrent.target.value)) {
        eventRefCurrent.target.value = eventRefCurrent.target.value.trim();
      }
    }
  });

  /**
   * Displaying a user error message
   */
  const showError = meta.touched && !!meta.error;

  const { value } = field;

  // Effect to update the input field internal value
  useEffect(() => {
    if (value) {
      setInnerValue(value as string);
    } else {
      setInnerValue('');
    }
  }, [value]);

  /**
   * Event handler when input value gets changed
   */
  const handleOnChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    event.persist();

    const newValue = event.currentTarget.value;
    setInnerValue(newValue);
    changeEventRef.current = event;
  }, []);

  return (
    <TextArea
      {...props}
      name={field.name}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      error={showError ? meta.error : undefined}
      value={innerValue}
    />
  );
};

export default TextAreaWithFormik;
